import React, { useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  CircularProgress,
  ListItemText,
  SelectPlaceholder,
} from '@/shared/ui';
import {
  dentalChairAPI,
  type DentalChair,
} from '@/modules/clinic/api/dentalChair.api';
import type { BookingChairOption } from '@/modules/scheduling/api/availability.api';
import { queryKeys } from '@/shared/query';
import { getBookingStatusVisual } from '../utils/bookingStatusVisual';

interface ChairSelectProps {
  clinicId: string;
  value: string;
  onChange: (chairId: string) => void;
  disabled?: boolean;
  error?: string | null;
  required?: boolean;
  includeInactiveIds?: string[];
  onActiveCountChange?: (count: number) => void;
  /** Annotated chairs from booking-options when date+time known. */
  bookingChairs?: BookingChairOption[];
}

const ChairSelect: React.FC<ChairSelectProps> = ({
  clinicId,
  value,
  onChange,
  disabled = false,
  error,
  required = false,
  includeInactiveIds = [],
  onActiveCountChange,
  bookingChairs,
}) => {
  const { t } = useTranslation(['appointment', 'clinic']);
  const needsInactive = includeInactiveIds.length > 0;

  const chairsQuery = useQuery({
    queryKey: queryKeys.clinic.chairs(clinicId, { includeInactive: needsInactive }),
    queryFn: async () => {
      const response = await dentalChairAPI.getAll(clinicId, needsInactive);
      return response.data ?? [];
    },
    enabled: Boolean(clinicId),
  });

  const chairs = useMemo(() => {
    const all = chairsQuery.data ?? [];
    const active = all.filter((item) => item.isActive);
    const extras = all.filter(
      (item) => !item.isActive && includeInactiveIds.includes(item.id)
    );
    const byId = new Map<string, DentalChair>();
    [...active, ...extras].forEach((item) => byId.set(item.id, item));
    return Array.from(byId.values()).sort((a, b) => a.sortOrder - b.sortOrder);
  }, [chairsQuery.data, includeInactiveIds.join(',')]);

  const statusById = useMemo(() => {
    const next = new Map<string, BookingChairOption>();
    for (const chair of bookingChairs ?? []) {
      next.set(chair.id, chair);
    }
    return next;
  }, [bookingChairs]);

  const activeCount = useMemo(
    () => (chairsQuery.data ?? []).filter((item) => item.isActive).length,
    [chairsQuery.data]
  );

  useEffect(() => {
    onActiveCountChange?.(clinicId ? activeCount : 0);
  }, [clinicId, activeCount, onActiveCountChange]);

  if (!clinicId) {
    return (
      <FormControl fullWidth disabled>
        <InputLabel>{t('fields.chair')}</InputLabel>
        <Select
          value=""
          label={t('fields.chair')}
          displayEmpty
          renderValue={() => (
            <SelectPlaceholder>{t('fallbacks.selectClinicFirst')}</SelectPlaceholder>
          )}
        >
          <MenuItem value="">{t('fallbacks.selectClinicFirst')}</MenuItem>
        </Select>
      </FormControl>
    );
  }

  if (chairsQuery.isPending) {
    return <CircularProgress size={24} />;
  }

  return (
    <FormControl fullWidth required={required} error={Boolean(error)} disabled={disabled}>
      <InputLabel required={required}>{t('fields.chair')}</InputLabel>
      <Select
        value={value}
        label={t('fields.chair')}
        displayEmpty
        renderValue={(selected) => {
          if (!selected) {
            return <SelectPlaceholder>{t('fields.chairPlaceholder')}</SelectPlaceholder>;
          }
          const chair = chairs.find((item) => item.id === selected);
          if (!chair) return String(selected);
          return chair.isActive
            ? chair.name
            : `${chair.name} (${t('clinic:chairs.status.inactive')})`;
        }}
        onChange={(e) => onChange(String(e.target.value))}
      >
        <MenuItem value="">{t('fields.chairPlaceholder')}</MenuItem>
        {chairs.map((chair) => {
          const annotated = statusById.get(chair.id);
          const visual = getBookingStatusVisual(annotated?.status);
          const disabledOption = Boolean(annotated?.status) && !visual.selectable;
          const statusSuffix =
            annotated?.status && annotated.status !== 'available'
              ? ` — ${t(visual.labelKey as any)}`
              : '';
          return (
            <MenuItem key={chair.id} value={chair.id} disabled={disabledOption}>
              <ListItemText
                primary={`${chair.name}${!chair.isActive ? ` (${t('clinic:chairs.status.inactive')})` : ''}${statusSuffix}`}
                secondary={annotated?.message}
              />
            </MenuItem>
          );
        })}
      </Select>
      {error && <FormHelperText>{error}</FormHelperText>}
    </FormControl>
  );
};

export default ChairSelect;
